import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useAlert } from '../../../contexts/AlertContext';
import { feedbackApi } from '../../../services/api';
import EmptyState from '../../common/EmptyState';

/**
 * ADR-003 Action Item 11 — "Sessions with left employees" dashboard.
 *
 * Lists in-progress feedback sessions that still have assignments belonging to
 * employees who have left (resigned/cancelled), so an admin can find and clean
 * them up (Decision 1: Remove assignee, performed on the session report page).
 *
 * Phase-1: admin-only on the server; non-admins get a 403 which is surfaced as
 * an empty/permission state here.
 */
const STATUS_LABELS = {
  resigned: 'Resigned',
  cancelled: 'Cancelled',
};

const SessionsWithLeftEmployees = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    let active = true;
    feedbackApi
      .getSessionsWithLeftEmployees()
      .then((data) => {
        if (active) {
          setSessions(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        if (err.status === 403) {
          setForbidden(true);
        } else {
          showAlert(err.message || 'Failed to load sessions with left individuals', 'error');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [showAlert]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (forbidden) {
    return (
      <EmptyState
        title="Admin access required"
        description="Only administrators can review sessions with left individuals."
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PersonOffIcon sx={{ mr: 1, color: '#92400E' }} />
        <Typography variant="h6">Sessions with left individuals</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {sessions.length === 0 ? (
        <EmptyState
          title="Nothing to clean up"
          description="No in-progress sessions currently have assignments for individuals who have left."
        />
      ) : (
        sessions.map((session) => (
          <Paper key={session.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {session.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  {session.month}/{session.year} • {session.leftEmployees.length} left individual(s)
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/feedback-sessions/report/${session.id}`)}
              >
                Open report
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {session.leftEmployees.map((c) => (
                <Chip
                  key={c.assignmentId}
                  label={`${c.employee.fullName} — ${STATUS_LABELS[c.organizationalStatus] || c.organizationalStatus}`}
                  size="small"
                  sx={{
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B',
                    fontSize: '11px',
                  }}
                />
              ))}
            </Box>
          </Paper>
        ))
      )}
    </Box>
  );
};

export default SessionsWithLeftEmployees;
